/**
 * Auth UI configs: views, field states, decorative counts, setup copy.
 */
import { COUNTRY_CODE_MAP } from '@/constants/phone-countries-catalog';

/** Which auth card is visible on the login page. */
export type LoginView = 'login' | 'reset-password' | 'setup-password' | 'phone-setup';

/** Password-reset request lifecycle (idle → sending → success). */
export type ResetState = 'initial' | 'sending' | 'success';

/** Visual state for auth text inputs. */
export type AuthFieldState = 'default' | 'error' | 'success';

/** Invitation password form: first activation vs Firebase reset. */
export type SetupPasswordMode = 'activate' | 'reset';

/** Email + password + confirm seed for the invitation setup form. */
export interface SetupPasswordData {
  email: string;
  password: string;
  confirmPassword: string;
}

/** Title / subtitle / legal footer for {@link SetupPasswordMode}. */
export interface SetupPasswordCopy {
  title: string;
  subtitle: string;
  footer: string;
}

/** Empty invitation / reset password fields. */
export const EMPTY_SETUP_PASSWORD: SetupPasswordData = {
  email: '',
  password: '',
  confirmPassword: '',
};

/** Copy for the invitation password card. */
export const SETUP_PASSWORD_COPY: Record<SetupPasswordMode, SetupPasswordCopy> = {
  reset: {
    title: 'Réinitialisez votre mot de passe',
    subtitle: 'Créez un nouveau mot de passe sécurisé pour votre compte',
    footer: 'Votre mot de passe sera mis à jour après validation',
  },
  activate: {
    title: 'Définissez votre mot de passe',
    subtitle: 'Créez un mot de passe sécurisé pour votre compte',
    footer: "En activant votre compte, vous acceptez nos conditions d'utilisation",
  },
};

/** Login-form password floor (Firebase email/password), not the 8-char setup meter. */
export const LOGIN_PASSWORD_MIN_LENGTH = 6;

/** National number length before the identifier field is treated as valid. */
export const PHONE_IDENTIFIER_MIN_LENGTH = 10;

/** SMS OTP length for login and invitation phone-setup. */
export const OTP_LENGTH = 6;

/** Resend countdown on the invitation phone-setup card, in seconds. */
export const PHONE_SETUP_OTP_COUNTDOWN_S = 60;

/** Login background particle count. */
export const LOGIN_PARTICLE_COUNT = 20;

/** 404 / 403 decorative particle count. */
export const UNAUTH_PARTICLE_COUNT = 8;

/** Indices for the login background particles. */
export const LOGIN_PARTICLE_INDICES = Array.from(
  { length: LOGIN_PARTICLE_COUNT },
  (_, index) => index,
);

/** Indices for the 404 / 403 decorative particles. */
export const UNAUTH_PARTICLE_INDICES = Array.from(
  { length: UNAUTH_PARTICLE_COUNT },
  (_, index) => index,
);

/** Longest-first dial codes so `+33` wins over `+3`. */
export const SORTED_DIAL_CODES = Object.keys(COUNTRY_CODE_MAP).sort(
  (left, right) => right.length - left.length,
);
